/**
 * @file One side of a point-in-time-recovery diff as a string on screen (SYD-2450).
 * Consumers: `FormRecoveryConfirmModalContent`, which names a restored row by its
 * label column, and `FormRecoveryChangeTable`, which fills the `Value now` and
 * `Value being restored` columns for a field, a reference and a cell alike.
 *
 * `FormPitrValue.value` is raw Firestore field data by design — whatever the key
 * held — so every shape here is checked and none asserted. A date crosses the
 * callable boundary as `{ _seconds, _nanoseconds }`, and both halves are required to
 * be numbers before it is read as one: an ordinary object carrying a `seconds` key
 * would otherwise be shown as a 1970 date.
 *
 * `null` the object is a key absent on that side; anything the key held but which
 * holds nothing — `null`, `undefined`, `''`, an empty array — is a key that was
 * present and cleared. The two read differently on purpose: `Not set` against
 * `Empty`. Neither may ever render as blank: these strings authorise a destructive
 * write, and an empty cell reads as no change at all.
 */

import dayjs from 'dayjs';

import type { FormPitrValue } from '#types';

/** A restored timestamp is named in full: it may be days back. */
const VALUE_TIME_FORMAT = 'MMM D, YYYY h:mm:ss A';

/**
 * The seconds of a Firestore timestamp, in either spelling, and only when its
 * nanoseconds sibling is a number too — the pair is what makes it a timestamp.
 */
const timestampSecondsOf = (value: object): number | undefined => {
  const bag = value as Record<string, unknown>;
  if (typeof bag.seconds === 'number' && typeof bag.nanoseconds === 'number') return bag.seconds;
  if (typeof bag._seconds === 'number' && typeof bag._nanoseconds === 'number') return bag._seconds;
  return undefined;
};

export const formatPitrValue = (side: FormPitrValue | null): string => {
  if (side === null) return 'Not set';
  const { value } = side;
  if (value === null || value === undefined || value === '') return 'Empty';
  if (Array.isArray(value)) {
    if (value.length === 0) return 'Empty';
    return value.map((item) => formatPitrValue({ value: item })).join(', ');
  }
  if (typeof value === 'object') {
    const seconds = timestampSecondsOf(value);
    if (seconds !== undefined) return dayjs(seconds * 1000).format(VALUE_TIME_FORMAT);
    const name = (value as { entityRecordName?: unknown }).entityRecordName;
    if (typeof name === 'string') return name;
    return JSON.stringify(value);
  }
  return String(value);
};
